import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/index';
import { getItemsWithPagination, getItem360List, resetPagination } from '../store/slices/itemSlice';
import { getItemCategories } from '../store/slices/itemCategorySlice';
import { Layout } from './CoreComponents';
import ItemView from './Shared/ItemView';
import { formatDate } from '../utils/formatterDate';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import BaseGrid from './Shared/BaseGrid';
import { AgGridReact } from 'ag-grid-react';

interface ProductInquiryPageProps {}

const ProductInquiryPage: React.FC<ProductInquiryPageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const gridRef = useRef<AgGridReact>(null);
  const { items, loading, error, pagination } = useSelector((state: RootState) => state.item);
  const { categories: itemCategories } = useSelector((state: RootState) => state.itemCategory);
  
  const [mainItem360Data, setMainItem360Data] = useState<any>(null);
  const [mainItem360Loading, setMainItem360Loading] = useState(false);
  
  const [productItem360Data, setProductItem360Data] = useState<any>(null);
  const [productItem360Loading, setProductItem360Loading] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [showItemInquiry, setShowItemInquiry] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<any>(null);
  const [showItemView, setShowItemView] = useState(false);
  const [itemToView, setItemToView] = useState<any>(null);
  const [showProductDetailModal, setShowProductDetailModal] = useState(false);
  const [productToView, setProductToView] = useState<any>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Load items and categories on component mount
  useEffect(() => {
    dispatch(resetPagination());
    setCurrentPage(1);
    dispatch(getItemCategories({}));
    dispatch(getItemsWithPagination({ 
      Page: 1, 
      PageSize: 50
    }));
  }, [dispatch]);

  // Search / refresh: fetch with SearchKeyword parameter
  const handleSearch = useCallback(async () => {
    dispatch(resetPagination());
    setCurrentPage(1);
    const requestParams: any = { Page: 1, PageSize: 50 };
    if (selectedCategoryId !== null) {
      requestParams.CategoryId = selectedCategoryId;
    }
    if (searchKeyword && searchKeyword.trim() !== '') {
      requestParams.SearchKeyword = searchKeyword.trim();
    }
    try {
      await dispatch(getItemsWithPagination(requestParams)).unwrap();
    } catch (error) {
      console.error('Search error:', error);
    }
  }, [dispatch, selectedCategoryId, searchKeyword]);

  const loadMoreItems = useCallback(async () => {
    if (isLoadingMore || !pagination.hasMore) return;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const requestParams: any = { Page: nextPage, PageSize: 50 };
      if (selectedCategoryId !== null) requestParams.CategoryId = selectedCategoryId;
      if (searchKeyword && searchKeyword.trim() !== '') {
        requestParams.SearchKeyword = searchKeyword.trim();
      }
      await dispatch(getItemsWithPagination(requestParams)).unwrap();
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, currentPage, pagination.hasMore, isLoadingMore, selectedCategoryId, searchKeyword]);

  const filteredItems = React.useMemo(() => {
    return items;
  }, [items]);

  const openItemInquiry = useCallback(async (itemCode: string | null | undefined) => {
    if (!itemCode) return;
    const request = items.find(p => p.itemCode === itemCode);
    if (request && request.id) {
      setCurrentProduct(request);
      setShowItemInquiry(true);
      setMainItem360Loading(true);
      
      try {
        const resultAction = await dispatch(getItem360List({ 
          id: request.id,
          filterId: request.id // Aynı ID'yi filtre olarak da kullan
        }));
        
        if (getItem360List.fulfilled.match(resultAction)) {
          const responseData = resultAction.payload;
          if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            setMainItem360Data(responseData.data);
          } else {
            setMainItem360Data(responseData);
          }
        }
      } catch (error) {
        console.error('Error loading item 360 data:', error);
      } finally {
        setMainItem360Loading(false);
      }
    }
  }, [items, dispatch]);

  const closeItemInquiry = () => {
    setShowItemInquiry(false);
    setCurrentProduct(null);
    setMainItem360Data(null);
    setMainItem360Loading(false);
  };

  const openItemView = useCallback((item: any) => {
    setItemToView(item);
    setShowItemView(true);
  }, []);

  const closeItemView = () => {
    setShowItemView(false);
    setItemToView(null);
  };

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'Stok Kodu',
      field: 'itemCode',
      minWidth: 130,
      flex: 1,
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 100,
      flex: 0.8,
    },
    {
      headerName: 'Kök Kod',
      valueGetter: (params) => params.data?.['rootCode'] || '-',
      minWidth: 90,
      flex: 0.7,
    },
    {
      headerName: 'Grup İsmi',
      field: 'categoryName',
      minWidth: 140,
      flex: 1.2,
    },
    {
      headerName: 'Ürün Adı',
      field: 'itemName',
      minWidth: 220,
      flex: 2,
      tooltipField: 'itemName',
      cellRenderer: (params: ICellRendererParams) => (
        <span
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
          onClick={() => params.data && openItemView(params.data)}
        >
          {params.value}
        </span>
      ),
    },
    {
      headerName: 'Birim',
      field: 'unit',
      minWidth: 70,
      flex: 0.5,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'KDV Oranı',
      field: 'vatRate',
      minWidth: 90,
      flex: 0.6,
      valueFormatter: (params) => `%${params.value || 0}`,
    },
    {
      headerName: 'Durum',
      field: 'inActive',
      minWidth: 80,
      flex: 0.6,
      cellRenderer: (params: ICellRendererParams) => {
        const isInactive = params.value;
        return (
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
            isInactive
              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
          }`}>
            {isInactive ? 'Pasif' : 'Aktif'}
          </span>
        );
      },
    },
    {
      headerName: 'İşlem',
      sortable: false,
      filter: false,
      resizable: false,
      minWidth: 80,
      flex: 0.5,
      cellRenderer: (params: ICellRendererParams) => (
        <button
          onClick={() => params.data && openItemInquiry(params.data.itemCode)}
          className="bg-primary text-white px-2 py-0.5 rounded text-xs hover:bg-blue-700 transition-colors"
        >
          Detay
        </button>
      ),
    },
  ], [openItemView, openItemInquiry]);

  const openProductDetailModal = async (product: any) => {
    if (!product) return;
    
    setProductToView(product);
    setShowProductDetailModal(true);
    setProductItem360Loading(true);
    
    try {
      if (product.itemId) {
        const resultAction = await dispatch(getItem360List({ 
          id: product.itemId,
          filterId: product.itemId
        }));
        
        if (getItem360List.fulfilled.match(resultAction)) {
          const responseData = resultAction.payload;
          if (responseData && typeof responseData === 'object' && 'data' in responseData) {
            setProductItem360Data(responseData.data);
          } else {
            setProductItem360Data(responseData);
          }
        }
      } else if (product.productCode) {
        const item = items.find(p => p.itemCode === product.productCode);
        if (item && item.id) {
          const resultAction = await dispatch(getItem360List({ 
            id: item.id,
            filterId: item.id
          }));
          
          if (getItem360List.fulfilled.match(resultAction)) {
            const responseData = resultAction.payload;
            if (responseData && typeof responseData === 'object' && 'data' in responseData) {
              setProductItem360Data(responseData.data);
            } else {
              setProductItem360Data(responseData);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading product 360 data:', error);
    } finally {
      setProductItem360Loading(false);
    }
  };

  const closeProductDetailModal = () => {
    setShowProductDetailModal(false);
    setProductToView(null);
    setProductItem360Data(null);
    setProductItem360Loading(false);
  };


  return (
    <Layout  currentScreen="product-inquiry">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        {/* Search bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-wrap items-end gap-3">
            <input 
              type="text" 
              placeholder="Stok Kodu, Kısa Kod, Kök Kod veya Ürün Adı ile Ara..." 
              value={searchKeyword} 
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm" 
            />
            <select
              value={selectedCategoryId || ''}
              onChange={(e) => {
                const newCategoryId = e.target.value ? parseInt(e.target.value) : null;
                setSelectedCategoryId(newCategoryId);
                dispatch(resetPagination());
                setCurrentPage(1);
                const requestParams: any = { Page: 1, PageSize: 50 };
                if (newCategoryId !== null) requestParams.CategoryId = newCategoryId;
                if (searchKeyword && searchKeyword.trim() !== '') {
                  requestParams.SearchKeyword = searchKeyword.trim();
                }
                dispatch(getItemsWithPagination(requestParams));
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Tüm Kategoriler</option>
              {itemCategories?.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
            <button onClick={handleSearch} disabled={loading && !showItemInquiry && !showProductDetailModal} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 flex-shrink-0">
              {loading && !showItemInquiry && !showProductDetailModal ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-search mr-1"></i>} Ara
            </button>
          </div>
        </div>


        {/* Loading State - Only show when modal is not open */}
        {loading && !showItemInquiry && !showProductDetailModal && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Ürünler yükleniyor...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
              <span className="text-red-700 dark:text-red-400">{error}</span>
            </div>
          </div>
        )}

        {/* Products Table - Show even when loading if modal is open */}
        {(!loading || showItemInquiry || showProductDetailModal) && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 flex flex-col flex-1 min-h-0 max-h-[calc(100vh-200px)] min-h-[300px]">
          {/* Mobile Card Layout */}
          <div className="lg:hidden overflow-auto">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredItems.map((item) => (
                <div key={item.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h3 
                        className="text-lg font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline transition-colors"
                        onClick={() => openItemView(item)}
                      >
                        {item.itemName}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{item.itemCode}</p>
                    </div>
                    <button 
                      onClick={() => openItemInquiry(item.itemCode)}
                      className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      Detay
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kısa Kod:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.shortCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Kök Kod:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item['rootCode'] || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Grup:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.categoryName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Birim:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.unit || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">KDV Oranı:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">%{item.vatRate || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Durum:</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.inActive ? 'Pasif' : 'Aktif'}</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More Button */}
              {pagination.hasMore && (
                <div className="px-4 py-4 text-center border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={loadMoreItems}
                    disabled={isLoadingMore}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                  >
                    {isLoadingMore ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Yükleniyor...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus"></i>
                        Daha Fazla Yükle ({pagination.totalCount - items.length} kaldı)
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Desktop AG Grid */}
          <div className="hidden lg:flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 relative" style={{ minHeight: '300px' }}>
              <div className="absolute inset-0">
                <BaseGrid
                  ref={gridRef}
                  gridStateKey="ag-grid-product-inquiry"
                  rowData={filteredItems}
                  columnDefs={columnDefs}
                  getRowId={(params) => String(params.data.id)}
                  domLayout="normal"
                />
              </div>
            </div>
            {pagination.hasMore && (
              <div className="px-4 py-3 text-center border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 flex-shrink-0">
                <button
                  onClick={loadMoreItems}
                  disabled={isLoadingMore}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                >
                  {isLoadingMore ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Yükleniyor...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus"></i>
                      Daha Fazla Yükle ({pagination.totalCount - items.length} kaldı)
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Item Inquiry Section */}
      {showItemInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 w-full max-w-[95vw] sm:max-w-[900px] mx-auto max-h-[90vh] overflow-y-auto transition-colors duration-300">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">Ürün Analizi - 360°</h3>
              <button
                onClick={closeItemInquiry}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            <div className="space-y-3">
              {/* Product Header - Stok Adı */}
              <div className="text-left pb-2 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white transition-colors">
                  {currentProduct?.itemName || 'Stok Adı'}
                </h2>
              </div>

              {/* Product Information */}
              <div className="text-xss text-gray-600 dark:text-gray-400 transition-colors">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div><span>KISA KOD: <span className="font-medium text-gray-800 dark:text-white transition-colors">{currentProduct?.shortCode || '-'}</span></span></div>
                  <div><span>ÜRÜN KODU: <span className="font-medium text-gray-800 dark:text-white transition-colors">{currentProduct?.itemCode}</span></span></div>
                  <div><span>GRUP İSMİ: <span className="font-medium text-gray-800 dark:text-white transition-colors">{currentProduct?.categoryName}</span></span></div>
                </div>
              </div>

              {/* Tüketim/Satış Analizi */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                  {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 ? 'Tüketim Analizi' : 'Satış Analizi'}
                  {mainItem360Loading && (
                    <span className="ml-2 text-xs text-blue-600">
                      <i className="fas fa-spinner fa-spin mr-1"></i>Yükleniyor...
                    </span>
                  )}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 dark:border-gray-600">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-600">
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Dönem</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q1</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q2</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q3</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q4</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white dark:bg-gray-800">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Geçen Yıl</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q1 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q2 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q3 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.q4 ? Number(mainItem360Data.consumptionAnalysis.lastYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {mainItem360Data?.consumptionAnalysis?.lastYear?.total ? Number(mainItem360Data.consumptionAnalysis.lastYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Bu Yıl</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q1 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q2 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q3 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.q4 ? Number(mainItem360Data.consumptionAnalysis.thisYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {mainItem360Data?.consumptionAnalysis?.thisYear?.total ? Number(mainItem360Data.consumptionAnalysis.thisYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-blue-50 dark:bg-blue-900/20">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Değişim (%)</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q1 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q1}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q2 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q2}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q3 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q3}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.q4 ? `${mainItem360Data.consumptionAnalysis.changePercentage.q4}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                          {mainItem360Data?.consumptionAnalysis?.changePercentage?.total ? `${mainItem360Data.consumptionAnalysis.changePercentage.total}%` : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Anlık Bilgiler */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">Anlık Bilgiler</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Stok</label>
                    <span className="text-base font-bold text-green-600">
                      {mainItem360Data?.instantInfo?.onHand ? Number(mainItem360Data.instantInfo.onHand).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Satınalma Siparişi</label>
                    <span className="text-base font-bold text-orange-600">
                      {mainItem360Data?.instantInfo?.openPurchaseBackOrder ? Number(mainItem360Data.instantInfo.openPurchaseBackOrder).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Talep</label>
                    <span className="text-base font-bold text-blue-600">
                      {mainItem360Data?.instantInfo?.openRequest ? Number(mainItem360Data.instantInfo.openRequest).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">
                      {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 ? 'Açık Üretim İş Emri' : 'Açık Müşteri Siparişi'}
                    </label>
                    <span className="text-base font-bold text-indigo-600">
                      {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 
                        ? (mainItem360Data?.instantInfo?.openProductionWO ? Number(mainItem360Data.instantInfo.openProductionWO).toLocaleString('tr-TR') : '0')
                        : (mainItem360Data?.instantInfo?.openSalesBackOrder ? Number(mainItem360Data.instantInfo.openSalesBackOrder).toLocaleString('tr-TR') : '0')
                      }
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Durum</label>
                    <span className="text-base font-bold text-purple-600">
                      {(() => {
                        if (!mainItem360Data?.instantInfo) return '0';
                        
                        const stok = mainItem360Data.instantInfo.onHand || 0;
                        const acikSatinalma = mainItem360Data.instantInfo.openPurchaseBackOrder || 0;
                        const acikTalep = mainItem360Data.instantInfo.openRequest || 0;
                        const acikMusteri = mainItem360Data.instantInfo.openSalesBackOrder || 0;
                        const acikUretim = mainItem360Data.instantInfo.openProductionWO || 0;
                        
                        // Durum = stok + açık satınalma siparişi + açık talep - açık müşteri siparişi/açık üretim iş emri
                        const cikarilacak = mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 ? acikUretim : acikMusteri;
                        const durum = stok + acikSatinalma + acikTalep - cikarilacak;
                        
                        // Negatif değerler için başına "-" işareti ekle
                        const formattedValue = Math.abs(durum).toLocaleString('tr-TR');
                        return durum < 0 ? `-${formattedValue}` : formattedValue;
                      })()}
                    </span>
                  </div>
                </div>
              </div>


              {/* Son Satın Alma Fiyatı - Metin Formatı */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  {(() => {
                    const lastPurchasePrice = mainItem360Data?.lastPurchasePrice;
                    const lastPurchaseDate = mainItem360Data?.lastPurchaseDate;
                    const lastPurchaseCurrency = mainItem360Data?.lastPurchaseCurrency;
                    const unitPriceUSDValue = mainItem360Data?.unitPriceUSDValue;
                    
                    if (lastPurchasePrice && lastPurchaseCurrency) {
                      return (
                        <>
                          Bu ürün <span className="font-semibold text-blue-600">{parseFloat(String(lastPurchasePrice))} {lastPurchaseCurrency}</span>
                          {unitPriceUSDValue && (
                            <> (<span className="font-semibold text-green-600">${parseFloat(String(unitPriceUSDValue)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>)</>
                          )}
                          {lastPurchaseDate && lastPurchaseDate !== '0001-01-01T00:00:00' ? (
                            <> fiyatında <span className="font-semibold">{formatDate(lastPurchaseDate)}</span> tarihinde satın alınmıştır.</>
                          ) : (
                            <> fiyatında satın alınmıştır.</>
                          )}
                        </>
                      );
                    } else {
                      return <span className="text-gray-500 dark:text-gray-400">Son satın alma bilgisi bulunmamaktadır.</span>;
                    }
                  })()}
                </div>
              </div>


              {/* İlişkili Mamuller - Sadece veri varsa göster */}
              {mainItem360Data?.relatedProducts && mainItem360Data.relatedProducts.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">İlişkili Mamuller</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-100 dark:bg-gray-600">
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Mamul Kodu</th>
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Mamul Adı</th>
                          <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Miktar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mainItem360Data.relatedProducts.map((product: any, index: number) => (
                          <tr key={index} className="bg-white dark:bg-gray-800">
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 transition-colors">
                              {product.productCode || '-'}
                            </td>
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 transition-colors">
                              <span 
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer underline"
                                onClick={() => openProductDetailModal(product)}
                              >
                                {product.productName || '-'}
                              </span>
                            </td>
                            <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                              {product.quantity ? Number(product.quantity).toLocaleString('tr-TR') : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ItemView Modal */}
      {showItemView && itemToView && (
        <ItemView
          isOpen={showItemView}
          onClose={closeItemView}
          itemId={itemToView.id}
          mode="view"
        />
      )}

      {/* Product Detail Modal */}
      {showProductDetailModal && productToView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 w-full max-w-[95vw] sm:max-w-[900px] mx-auto max-h-[90vh] overflow-y-auto transition-colors duration-300">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white transition-colors">Ürün Analizi - 360°</h3>
              <button
                onClick={closeProductDetailModal}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            <div className="space-y-3">
              {/* Product Header - Stok Adı */}
              <div className="text-left pb-2 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white transition-colors">
                  {productItem360Data?.itemName || productToView?.productName || 'Stok Adı'}
                </h2>
              </div>

              {/* Product Information */}
              <div className="text-xs text-gray-600 dark:text-gray-400 transition-colors">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  <div><span>KISA KOD: <span className="font-medium text-gray-800 dark:text-white transition-colors">{productItem360Data?.shortCode || '-'}</span></span></div>
                  <div><span>ÜRÜN KODU: <span className="font-medium text-gray-800 dark:text-white transition-colors">{productItem360Data?.itemCode || productToView?.productCode || '-'}</span></span></div>
                  <div><span>GRUP İSMİ: <span className="font-medium text-gray-800 dark:text-white transition-colors">{productItem360Data?.categoryName || '-'}</span></span></div>
                </div>
              </div>

              {/* Tüketim/Satış Analizi */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">
                  {productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 ? 'Tüketim Analizi' : 'Satış Analizi'}
                  {productItem360Loading && (
                    <span className="ml-2 text-xs text-blue-600">
                      <i className="fas fa-spinner fa-spin mr-1"></i>Yükleniyor...
                    </span>
                  )}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 dark:border-gray-600">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-600">
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-left transition-colors">Dönem</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q1</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q2</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q3</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Q4</th>
                        <th className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center transition-colors">Toplam</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white dark:bg-gray-800">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Geçen Yıl</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q1 ? Number(productItem360Data.consumptionAnalysis.lastYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q2 ? Number(productItem360Data.consumptionAnalysis.lastYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q3 ? Number(productItem360Data.consumptionAnalysis.lastYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.q4 ? Number(productItem360Data.consumptionAnalysis.lastYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {productItem360Data?.consumptionAnalysis?.lastYear?.total ? Number(productItem360Data.consumptionAnalysis.lastYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Bu Yıl</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q1 ? Number(productItem360Data.consumptionAnalysis.thisYear.q1).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q2 ? Number(productItem360Data.consumptionAnalysis.thisYear.q2).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q3 ? Number(productItem360Data.consumptionAnalysis.thisYear.q3).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center transition-colors">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.q4 ? Number(productItem360Data.consumptionAnalysis.thisYear.q4).toLocaleString('tr-TR') : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-blue-600 text-center">
                          {productItem360Data?.consumptionAnalysis?.thisYear?.total ? Number(productItem360Data.consumptionAnalysis.thisYear.total).toLocaleString('tr-TR') : '-'}
                        </td>
                      </tr>
                      <tr className="bg-blue-50 dark:bg-blue-900/20">
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-900 dark:text-white transition-colors">Değişim (%)</td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q1 ? `${productItem360Data.consumptionAnalysis.changePercentage.q1}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q2 ? `${productItem360Data.consumptionAnalysis.changePercentage.q2}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q3 ? `${productItem360Data.consumptionAnalysis.changePercentage.q3}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.q4 ? `${productItem360Data.consumptionAnalysis.changePercentage.q4}%` : '-'}
                        </td>
                        <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                          {productItem360Data?.consumptionAnalysis?.changePercentage?.total ? `${productItem360Data.consumptionAnalysis.changePercentage.total}%` : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Anlık Bilgiler */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 sm:p-3 rounded-lg transition-colors duration-300">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 transition-colors">Anlık Bilgiler</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Stok</label>
                    <span className="text-base font-bold text-green-600">
                      {productItem360Data?.instantInfo?.onHand ? Number(productItem360Data.instantInfo.onHand).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Satınalma Siparişi</label>
                    <span className="text-base font-bold text-orange-600">
                      {productItem360Data?.instantInfo?.openPurchaseBackOrder ? Number(productItem360Data.instantInfo.openPurchaseBackOrder).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Açık Talep</label>
                    <span className="text-base font-bold text-blue-600">
                      {productItem360Data?.instantInfo?.openRequest ? Number(productItem360Data.instantInfo.openRequest).toLocaleString('tr-TR') : '-'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">
                      {productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 ? 'Açık Üretim İş Emri' : 'Açık Müşteri Siparişi'}
                    </label>
                    <span className="text-base font-bold text-indigo-600">
                      {productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0
                        ? (productItem360Data?.instantInfo?.openProductionWO ? Number(productItem360Data.instantInfo.openProductionWO).toLocaleString('tr-TR') : '0')
                        : (productItem360Data?.instantInfo?.openSalesBackOrder ? Number(productItem360Data.instantInfo.openSalesBackOrder).toLocaleString('tr-TR') : '0')
                      }
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5 transition-colors">Durum</label>
                    <span className="text-base font-bold text-purple-600">
                      {(() => {
                        if (!productItem360Data?.instantInfo) return '0';

                        const stok = productItem360Data.instantInfo.onHand || 0;
                        const acikSatinalma = productItem360Data.instantInfo.openPurchaseBackOrder || 0;
                        const acikTalep = productItem360Data.instantInfo.openRequest || 0;
                        const acikMusteri = productItem360Data.instantInfo.openSalesBackOrder || 0;
                        const acikUretim = productItem360Data.instantInfo.openProductionWO || 0;

                        // Durum = stok + açık satınalma siparişi + açık talep - açık müşteri siparişi/açık üretim iş emri
                        const cikarilacak = productItem360Data?.relatedProducts && productItem360Data.relatedProducts.length > 0 ? acikUretim : acikMusteri;
                        const durum = stok + acikSatinalma + acikTalep - cikarilacak;

                        // Negatif değerler için başına "-" işareti ekle
                        const formattedValue = Math.abs(durum).toLocaleString('tr-TR');
                        return durum < 0 ? `-${formattedValue}` : formattedValue;
                      })()}
                    </span>
                  </div>
                </div>
              </div>


              {/* Son Satın Alma Fiyatı - Metin Formatı */}
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  {(() => {
                    const lastPurchasePrice = productItem360Data?.lastPurchasePrice;
                    const lastPurchaseDate = productItem360Data?.lastPurchaseDate;
                    const lastPurchaseCurrency = productItem360Data?.lastPurchaseCurrency;
                    const unitPriceUSDValue = productItem360Data?.unitPriceUSDValue;
                    
                    if (lastPurchasePrice && lastPurchaseCurrency) {
                      return (
                        <>
                          Bu ürün <span className="font-semibold text-blue-600">{parseFloat(String(lastPurchasePrice))} {lastPurchaseCurrency}</span>
                          {unitPriceUSDValue && (
                            <> (<span className="font-semibold text-green-600">${parseFloat(String(unitPriceUSDValue)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>)</>
                          )}
                          {lastPurchaseDate && lastPurchaseDate !== '0001-01-01T00:00:00' ? (
                            <> fiyatında <span className="font-semibold">{formatDate(lastPurchaseDate)}</span> tarihinde satın alınmıştır.</>
                          ) : (
                            <> fiyatında satın alınmıştır.</>
                          )}
                        </>
                      );
                    } else {
                      return <span className="text-gray-500 dark:text-gray-400">Son satın alma bilgisi bulunmamaktadır.</span>;
                    }
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProductInquiryPage;
